import React, { useEffect, useMemo, useState } from 'react';
import { Loader2, Users, Settings2, Star, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { forecastAPI } from '@/lib/api';
import { formatCurrency } from '@/lib/utils';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';

const DOW_FULL = ['Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi', 'Dimanche'];
const DOW_SHORT = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'];

function toIso(d) { return d.toISOString().slice(0, 10); }
function mondayOf(iso) {
    const d = new Date(iso + 'T00:00:00');
    const diff = d.getDay() === 0 ? -6 : 1 - d.getDay();
    d.setDate(d.getDate() + diff);
    return toIso(d);
}
function addDays(iso, n) { const d = new Date(iso + 'T12:00:00'); d.setDate(d.getDate() + n); return d.toISOString().slice(0, 10); }

export default function StaffingWeek() {
    const { user } = useAuth();
    const allowedIds = user?.allowed_sites?.length ? user.allowed_sites : null;
    const [open, setOpen] = useState(() => localStorage.getItem('staffing_week_open') === '1');
    const [start, setStart] = useState(mondayOf(toIso(new Date())));
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [showAdvice, setShowAdvice] = useState(false);
    const [settingsOpen, setSettingsOpen] = useState(false);

    const toggleOpen = () => setOpen(v => {
        localStorage.setItem('staffing_week_open', v ? '0' : '1');
        return !v;
    });

    const load = async () => {
        setLoading(true);
        try {
            const r = await forecastAPI.staffingWeek({ start });
            setData(r.data);
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => { if (open) load(); /* eslint-disable-next-line */ }, [start, open]);

    const sites = useMemo(
        () => (data?.sites || []).filter(s => !allowedIds || allowedIds.includes(s.site_id)),
        [data, allowedIds]
    );
    const todayIso = toIso(new Date());

    return (
        <Card data-testid="staffing-week-section">
            <CardHeader className={`py-2 px-3 ${open ? 'border-b' : ''}`}>
                <div className="flex flex-wrap items-center gap-2">
                    <Users className="w-4 h-4 text-teal-600" />
                    <CardTitle className="text-sm">Semaine personnel</CardTitle>
                    {open && (
                    <div className="inline-flex items-center gap-0.5">
                        <button type="button" className="h-6 w-6 text-slate-400 hover:text-slate-700 text-sm" title="Semaine précédente"
                            onClick={() => setStart(s => addDays(s, -7))} data-testid="staffing-prev-week">‹</button>
                        <Badge variant="outline" className="text-[10px] font-mono text-slate-600" data-testid="staffing-week-label">
                            {data ? `${data.start.slice(8, 10)}/${data.start.slice(5, 7)} → ${data.end.slice(8, 10)}/${data.end.slice(5, 7)}` : '…'}
                        </Badge>
                        <button type="button" className="h-6 w-6 text-slate-400 hover:text-slate-700 text-sm" title="Semaine suivante"
                            onClick={() => setStart(s => addDays(s, 7))} data-testid="staffing-next-week">›</button>
                        <button type="button" className="h-6 px-1.5 text-[10px] font-semibold text-slate-500 hover:text-slate-800 border border-slate-200 rounded ml-1"
                            onClick={() => setStart(mondayOf(toIso(new Date())))} data-testid="staffing-this-week">Cette semaine</button>
                    </div>
                    )}
                    <div className="ml-auto flex items-center gap-2">
                        {open && (
                        <>
                        <label className={`inline-flex items-center gap-1 px-2 h-6 rounded-md border cursor-pointer text-[11px] font-semibold transition-colors ${showAdvice ? 'bg-amber-50 border-amber-300 text-amber-700' : 'bg-white border-slate-200 text-slate-500'}`}
                            data-testid="staffing-show-advice">
                            <Checkbox checked={showAdvice} onCheckedChange={() => setShowAdvice(v => !v)} className="w-3 h-3" />
                            Conseils CA
                        </label>
                        <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => setSettingsOpen(true)} data-testid="staffing-settings-btn">
                            <Settings2 className="w-3.5 h-3.5 mr-1" />Réglages
                        </Button>
                        </>
                        )}
                        <button
                            type="button"
                            onClick={toggleOpen}
                            className="text-[11px] font-semibold text-slate-500 hover:text-slate-800 border border-slate-200 rounded h-7 px-2 flex items-center gap-1"
                            data-testid="staffing-week-toggle"
                            title={open ? 'Masquer la semaine personnel' : 'Afficher la semaine personnel'}
                        >
                            {open ? '▼' : '▶'} {open ? 'Masquer' : 'Afficher'}
                        </button>
                    </div>
                </div>
            </CardHeader>
            {open && (
            <CardContent className="p-0">
                {loading && !data ? (
                    <div className="py-8 text-center text-slate-400"><Loader2 className="w-5 h-5 mx-auto animate-spin" /></div>
                ) : data && (
                    <div className="overflow-x-auto">
                        <table className="w-full text-[11px]">
                            <thead className="bg-slate-50 border-b text-[10px] uppercase text-slate-500">
                                <tr>
                                    <th className="text-left px-3 py-1.5 w-24">Site</th>
                                    {data.days.map((iso, i) => {
                                        const ctx = data.context[i];
                                        const isToday = iso === todayIso;
                                        return (
                                            <th key={iso} className={`text-center px-1 py-1.5 ${i >= 5 ? 'text-red-400' : ''} ${isToday ? 'bg-indigo-50' : ''}`}>
                                                <div>{DOW_SHORT[i]} {iso.slice(8, 10)}/{iso.slice(5, 7)}</div>
                                                {ctx.holiday && <div className="text-[8px] normal-case text-purple-500 font-semibold">{ctx.holiday.split('(')[0]}</div>}
                                                {ctx.vacation && !ctx.holiday && <div className="text-[8px] normal-case text-sky-500">Vac. {ctx.vacation}</div>}
                                            </th>
                                        );
                                    })}
                                </tr>
                            </thead>
                            <tbody>
                                {sites.map(site => (
                                    <tr key={site.site_id} className="border-b border-slate-100">
                                        <td className="px-3 py-1.5 font-semibold text-slate-700 whitespace-nowrap">
                                            {site.name.replace('FE ', '')}
                                            {site.has_manager && <Star className="inline w-3 h-3 ml-1 text-amber-400 fill-amber-400" title="Site avec responsable" />}
                                        </td>
                                        {site.cells.map(cell => (
                                            <StaffCell key={cell.date} cell={cell} showAdvice={showAdvice} isToday={cell.date === todayIso} />
                                        ))}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                        <div className="px-3 py-1.5 text-[10px] text-slate-400 italic border-t">
                            Chiffre principal : habitude saisie du site (★ = avec responsable). « Conseils CA » affiche la proposition
                            calculée depuis le CA prévu quand elle diffère. Journée type 7 h ; dimanche / férié = journée courte 5-6 h.
                        </div>
                    </div>
                )}
            </CardContent>
            )}
            <StaffingSettingsDialog open={settingsOpen} onOpenChange={setSettingsOpen} onSaved={load} />
        </Card>
    );
}

function StaffCell({ cell, showAdvice, isToday }) {
    const differs = !cell.closed && cell.suggested !== cell.habit;
    return (
        <td className={`text-center px-1 py-1.5 align-top ${isToday ? 'bg-indigo-50/60' : ''}`}
            data-testid={`staff-cell-${cell.date}`}>
            {cell.closed ? (
                <div className="text-slate-300 font-semibold">Fermé</div>
            ) : (
                <>
                    <div className="flex items-center justify-center gap-0.5">
                        <span className="text-base font-bold text-slate-800 tabular-nums">{cell.habit}</span>
                        {cell.habit_manager && <Star className="w-3 h-3 text-amber-400 fill-amber-400" title="Avec responsable" />}
                        {cell.alert && <AlertTriangle className="w-3.5 h-3.5 text-red-500" title={cell.alert} data-testid={`staff-alert-${cell.date}`} />}
                        {differs && !showAdvice && <span className="w-1.5 h-1.5 rounded-full bg-amber-400 mt-[-8px]" title={`Conseil CA : ${cell.suggested}`} />}
                    </div>
                    {showAdvice && (
                        <div className={`text-[9px] font-semibold ${differs ? 'text-amber-600' : 'text-slate-300'}`}>
                            conseil {cell.suggested}{cell.manager_suggested ? ' ★' : ''}
                        </div>
                    )}
                    <div className="text-[9px] text-slate-400 tabular-nums">
                        {cell.actual != null ? <span className="text-slate-600 font-semibold">{formatCurrency(Math.round(cell.actual))}</span> : `~${formatCurrency(cell.predicted)}`}
                    </div>
                    {cell.short_day && <div className="text-[8px] text-sky-500">journée courte</div>}
                </>
            )}
        </td>
    );
}

function StaffingSettingsDialog({ open, onOpenChange, onSaved }) {
    const [settings, setSettings] = useState(null);
    const [activeSiteId, setActiveSiteId] = useState(null);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (!open) return;
        forecastAPI.staffingSettings().then(r => {
            setSettings(r.data);
            setActiveSiteId(prev => prev || r.data[0]?.site_id);
        });
    }, [open]);

    const site = settings?.find(s => s.site_id === activeSiteId);
    const updateSite = (patch) => setSettings(list => list.map(s => s.site_id === activeSiteId ? { ...s, ...patch, _dirty: true } : s));

    const save = async () => {
        setSaving(true);
        try {
            for (const s of settings.filter(x => x._dirty)) {
                await forecastAPI.saveStaffingSettings(s.site_id, {
                    habits: s.habits,
                    min_by_weekday: s.min_by_weekday,
                    thresholds: s.thresholds_source === 'manual' ? s.thresholds : null,
                });
            }
            toast.success('Réglages personnel enregistrés');
            onOpenChange(false);
            onSaved && onSaved();
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Erreur à l\'enregistrement');
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-2xl" data-testid="staffing-settings-dialog">
                <DialogHeader>
                    <DialogTitle className="text-sm">Habitudes personnel par site</DialogTitle>
                    <DialogDescription className="text-[11px]">
                        Effectif habituel par jour, responsable ★, minimum imposé et paliers CA → effectif.
                    </DialogDescription>
                </DialogHeader>
                {!settings ? (
                    <div className="py-6 text-center text-slate-400"><Loader2 className="w-5 h-5 mx-auto animate-spin" /></div>
                ) : (
                    <div className="space-y-3">
                        <div className="flex flex-wrap gap-1">
                            {settings.map(s => (
                                <button key={s.site_id} type="button" onClick={() => setActiveSiteId(s.site_id)}
                                    className={`px-2.5 h-6 rounded-md border text-[11px] font-semibold ${s.site_id === activeSiteId ? 'bg-slate-900 text-white border-slate-900' : 'bg-white text-slate-600 border-slate-200'}`}
                                    data-testid={`staffing-settings-site-${s.site_name.replace('FE ', '').toLowerCase()}`}>
                                    {s.site_name.replace('FE ', '')}{s._dirty ? ' •' : ''}
                                </button>
                            ))}
                        </div>
                        {site && (
                            <>
                                <table className="w-full text-[11px]">
                                    <thead className="bg-slate-50 text-[10px] uppercase text-slate-500">
                                        <tr>
                                            <th className="text-left px-2 py-1">Jour</th>
                                            <th className="text-center px-2 py-1">Habitude (pers.)</th>
                                            <th className="text-center px-2 py-1">Responsable ★</th>
                                            <th className="text-center px-2 py-1" title="Effectif minimum imposé quel que soit le CA prévu">Minimum</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {DOW_FULL.map((day, i) => (
                                            <tr key={day} className="border-b border-slate-100">
                                                <td className={`px-2 py-1 font-medium ${i >= 5 ? 'text-red-500' : 'text-slate-700'}`}>{day}</td>
                                                <td className="text-center px-2 py-1">
                                                    <select value={site.habits[i].count}
                                                        onChange={e => updateSite({ habits: site.habits.map((h, j) => j === i ? { ...h, count: parseInt(e.target.value, 10) } : h) })}
                                                        className="border rounded px-1.5 py-0.5 text-[11px] bg-white"
                                                        data-testid={`habit-count-${i}`}>
                                                        {[0, 1, 2, 3, 4].map(n => <option key={n} value={n}>{n === 0 ? '0 (fermé)' : n}</option>)}
                                                    </select>
                                                </td>
                                                <td className="text-center px-2 py-1">
                                                    <Checkbox checked={site.habits[i].manager}
                                                        onCheckedChange={v => updateSite({ habits: site.habits.map((h, j) => j === i ? { ...h, manager: !!v } : h) })}
                                                        className="w-3.5 h-3.5" data-testid={`habit-manager-${i}`} />
                                                </td>
                                                <td className="text-center px-2 py-1">
                                                    <select value={site.min_by_weekday[i]}
                                                        onChange={e => updateSite({ min_by_weekday: site.min_by_weekday.map((m, j) => j === i ? parseInt(e.target.value, 10) : m) })}
                                                        className="border rounded px-1.5 py-0.5 text-[11px] bg-white"
                                                        data-testid={`habit-min-${i}`}>
                                                        {[0, 1, 2, 3].map(n => <option key={n} value={n}>{n === 0 ? '—' : n}</option>)}
                                                    </select>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                                <div className="flex flex-wrap items-center gap-2 text-[11px] bg-slate-50 rounded-md p-2">
                                    <span className="font-semibold text-slate-600">Paliers CA → effectif :</span>
                                    <span>1 pers. si &lt;</span>
                                    <input type="number" value={site.thresholds?.[0] ?? ''} min={1}
                                        onChange={e => updateSite({ thresholds: [parseInt(e.target.value || 0, 10), site.thresholds?.[1] || 0], thresholds_source: 'manual' })}
                                        className="w-20 border rounded px-1.5 py-0.5 tabular-nums" data-testid="threshold-t1" />
                                    <span>€ · 2 pers. si &lt;</span>
                                    <input type="number" value={site.thresholds?.[1] ?? ''} min={1}
                                        onChange={e => updateSite({ thresholds: [site.thresholds?.[0] || 0, parseInt(e.target.value || 0, 10)], thresholds_source: 'manual' })}
                                        className="w-20 border rounded px-1.5 py-0.5 tabular-nums" data-testid="threshold-t2" />
                                    <span>€ · sinon 3 pers.</span>
                                    <Badge variant="outline" className="text-[9px]">
                                        {site.thresholds_source === 'manual' ? 'ajusté à la main' : 'appris (MS 2026)'}
                                    </Badge>
                                    {site.thresholds_source === 'manual' && site.learned_thresholds && (
                                        <button type="button" className="text-[10px] text-indigo-600 underline"
                                            onClick={() => updateSite({ thresholds: site.learned_thresholds, thresholds_source: 'learned' })}
                                            data-testid="threshold-reset">
                                            revenir aux paliers appris ({site.learned_thresholds[0]} / {site.learned_thresholds[1]} €)
                                        </button>
                                    )}
                                    {site.cost_per_person && <span className="text-slate-400">coût pers./jour ≈ {formatCurrency(site.cost_per_person)}</span>}
                                </div>
                            </>
                        )}
                        <div className="flex justify-end gap-2">
                            <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => onOpenChange(false)}>Annuler</Button>
                            <Button size="sm" className="h-7 text-xs" onClick={save} disabled={saving || !settings.some(s => s._dirty)} data-testid="staffing-settings-save">
                                {saving && <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />}Enregistrer
                            </Button>
                        </div>
                    </div>
                )}
            </DialogContent>
        </Dialog>
    );
}
